import { useSelector } from '@tanstack/react-store'
import commentOrderStore from '@/utils/stores/commentOrder'
import AutoLoadMoreIndicator from '@/components/AutoLoadMoreIndicator'
import Comment, { type CommentData } from '@/components/Comment/Comment'
import { CommentActionsProvider } from '@/components/Comment/CommentActionsContext'
import {
	DELETE_COMMENT_MUTATION_OPTIONS,
	LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS,
	PIN_COMMENT_TOGGLE_MUTATION_OPTIONS,
} from '@/components/Comment/hooks'
import type { ReplyToComment } from '@/components/Comment/schema'
import { showCommentFormModal } from '@/components/Comment/utils'
import NavBar from '@/components/NavBar'
import Page from '@/components/Page'
import CommentListSection from '@/components/Post/CommentListSection'
import { checkIsInJikeApp } from '@/utils/env'
import {
	generateNonPrimaryCommentsQueryKey,
	getCommentQueryOptions,
	getNonPrimaryCommentsQueryOptions,
} from '@/utils/fetchers/comment'
import { fetchAndCachePost } from '@/utils/fetchers/post'
import useIsRouteCached from '@/utils/hooks/useIsRouteCached'
import { prepareToRefetchListQuery } from '@/utils/list'
import queryClient from '@/utils/queryClient'
import { POST_TYPE_TO_ROUTE_MAPPING, ROUTE_TO_POST_TYPE_MAPPING } from '@/utils/route'
import { Button } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { flatMap, map, pick } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useTitle } from 'react-use'
import styles from './$commentId.module.css'

export const Route = createFileRoute('/u/$username_/$type/$id/$commentId')({
	component: CommentPage,
	beforeLoad: async ({ matches }) => {
		checkIsInJikeApp(matches)
	},
	loaderDeps: ({ search }) => search,
	loader: async (ctx) => {
		const params = ctx.params as {
			id: string
			username: string
			commentId: string
			type: keyof typeof ROUTE_TO_POST_TYPE_MAPPING
		}
		const [post, comment] = await Promise.all([
			fetchAndCachePost(params.id, ROUTE_TO_POST_TYPE_MAPPING[params.type]),
			queryClient.ensureQueryData(
				getCommentQueryOptions({
					id: params.commentId,
					targetType: ROUTE_TO_POST_TYPE_MAPPING[params.type],
				})
			),
		])

		return {
			data: comment,
			post,
		}
	},
})

// comment page

function CommentPage() {
	const { commentId, id, username } = Route.useParams()
	const { data, post } = Route.useLoaderData()
	const isPostPageRouteCached = useIsRouteCached('/u/$username/$type/$id')
	const isViewPostButtonShow = !isPostPageRouteCached

	const order = useSelector(commentOrderStore)

	const {
		data: commentPages,
		fetchNextPage,
		isFetchingNextPage,
		refetch,
	} = useInfiniteQuery(
		getNonPrimaryCommentsQueryOptions({
			targetType: data.targetType,
			primaryCommentId: data.threadId,
			level: data.level,
			targetId: data.targetId,
			order,
		})
	)

	const comments = useMemo(
		() =>
			map(
				flatMap(commentPages?.pages, (page) => page.data),
				(item) => pick(item, ['id', 'targetType']) as CommentData
			),
		[commentPages?.pages]
	)

	const refresh = useCallback(() => {
		prepareToRefetchListQuery(
			generateNonPrimaryCommentsQueryKey({
				targetType: data.targetType,
				primaryCommentId: data.threadId,
				level: data.level,
				targetId: data.targetId,
				order,
			})
		)
		return refetch()
	}, [data, order, refetch])

	const handleComment = useCallback((toComment: ReplyToComment) => {
		const modalId = showCommentFormModal({
			replyToComment: toComment,
			onClose: () => {
				modals.close(modalId)
			},
			onSubmit: refresh,
		})
	}, [])
	const { mutateAsync: toggleLike } = useMutation(LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS)
	const { mutateAsync: togglePin } = useMutation(PIN_COMMENT_TOGGLE_MUTATION_OPTIONS)
	const { mutateAsync: deleteComment } = useMutation(DELETE_COMMENT_MUTATION_OPTIONS)

	useTitle(`${data.user.screenName}：${data.content} - 即刻`)

	if (!data) {
		return null
	}

	return (
		<Page header={<NavBar title="评论详情" />}>
			{isViewPostButtonShow && (
				<Link
					to="/u/$username/$type/$id"
					params={{ username, id, type: POST_TYPE_TO_ROUTE_MAPPING[post.type] }}
					className={styles.link}
				>
					<Button variant="outline" size="sm" className={styles.button}>
						查看原动态
					</Button>
				</Link>
			)}
			<div className={styles.container} key={data.id}>
				<CommentActionsProvider
					value={{
						refresh,
						onComment: handleComment,
						onDelete: deleteComment,
						onToggleLike: toggleLike,
						onTogglePin: togglePin,
					}}
				>
					<Comment
						noReplies
						data={{
							id: data.id,
							targetType: data.targetType,
						}}
						className={styles.comment}
						noSuffixTitle
					/>

					<CommentListSection data={comments} isLoading={isFetchingNextPage} />
				</CommentActionsProvider>
			</div>
			<AutoLoadMoreIndicator
				isLoading={isFetchingNextPage}
				onLoadMore={async () => void (await fetchNextPage())}
			/>
		</Page>
	)
}
